import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Tema UI applicabile a Diakonix Desktop.
 *
 * `light` è il default storico; `dark` è il tema introdotto in v2.14.0
 * insieme al design token system (Sprint 2 Track A). Se in futuro
 * arrivano altri temi (es. `high-contrast`), estendere questo union
 * e aggiungere gli override corrispondenti in `theme/globals.scss`.
 */
export type ThemeMode = 'light' | 'dark';

/**
 * `ThemeService` — Sprint 2 Track A · Fase 4 (2026-07-02, v2.14.0-dev).
 *
 * Gestione centralizzata del tema light/dark. Sostituisce il vecchio
 * pattern `document.body.classList.add('dark')` sparso in
 * `sidebar.component.ts` (rimosso in v2.13.0 W3). Il service è la
 * SOLA fonte di verità:
 *   - applica/rimuove la classe `.dark` sul `<body>`,
 *   - persiste la scelta in `localStorage['diakonix.theme']`,
 *   - fallback a `prefers-color-scheme` del sistema operativo se
 *     l'utente non ha mai settato una preferenza esplicita.
 *
 * Pattern d'uso:
 *   1. `AppComponent.ngOnInit()` chiama `themeService.init()` per
 *      applicare il tema al boot (letto da localStorage o system).
 *   2. Il toggle in sidebar chiama `themeService.toggle()` per
 *      invertire + persistere.
 *   3. Component che vogliono reagire al cambio tema si iscrivono
 *      a `themeService.currentTheme$`.
 *
 * NB: il fatto che il body abbia la classe `.dark` triggera gli
 * override CSS variables in `theme/globals.scss body.dark { ... }`,
 * che a loro volta cascade nei ~50 file component SCSS migrati a
 * `var(--token)` in Fase 3.
 *
 * @see src/documentation/DARK_THEME_REFACTOR_2026-07-01.md
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  /** Chiave localStorage per la persistenza. */
  private readonly STORAGE_KEY = 'diakonix.theme';

  /** Classe applicata a `<body>` per attivare il tema dark. */
  private readonly BODY_CLASS_DARK = 'dark';

  private readonly currentThemeSubject = new BehaviorSubject<ThemeMode>('light');

  /**
   * Stream reattivo del tema corrente. UI che deve aggiornare il
   * proprio stato (es. label del toggle "Tema chiaro" / "Tema scuro")
   * si iscrive qui. Vedi `sidebar.component.ts` per un esempio.
   */
  readonly currentTheme$: Observable<ThemeMode> = this.currentThemeSubject.asObservable();

  /** Getter sincrono del tema corrente. Utile per binding template diretti. */
  get currentTheme(): ThemeMode {
    return this.currentThemeSubject.value;
  }

  /**
   * Da chiamare in `AppComponent.ngOnInit()`. Applica il tema:
   *   1. se l'utente ha una scelta salvata in `localStorage`, usala;
   *   2. altrimenti, rispetta `prefers-color-scheme` del sistema;
   *   3. fallback finale: `light`.
   *
   * Idempotente — sicuro chiamarla più volte.
   */
  init(): void {
    const stored = this.readStoredTheme();
    if (stored) {
      this.apply(stored);
      return;
    }
    this.apply(this.prefersSystemDark() ? 'dark' : 'light');
  }

  /**
   * Inverte il tema corrente (`light` ↔ `dark`) e persiste.
   * Chiamato dal toggle sidebar.
   */
  toggle(): void {
    const next: ThemeMode = this.currentTheme === 'dark' ? 'light' : 'dark';
    this.apply(next);
  }

  /**
   * Set esplicito di un tema specifico. Public API per casi edge
   * (es. settings page con dropdown "chiaro / scuro / auto"). Il
   * caller normale usa `toggle()`.
   */
  apply(theme: ThemeMode): void {
    if (theme === 'dark') {
      document.body.classList.add(this.BODY_CLASS_DARK);
    } else {
      document.body.classList.remove(this.BODY_CLASS_DARK);
    }
    this.persistTheme(theme);
    this.currentThemeSubject.next(theme);
  }

  // ─── Helpers ─────────────────────────────────────────────────────

  /**
   * Legge la preferenza persistita. Ritorna `null` se localStorage
   * non è accessibile (private mode, quota exceeded) o se il valore
   * salvato non è riconosciuto (protezione contro dati corrotti).
   */
  private readStoredTheme(): ThemeMode | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored === 'dark' || stored === 'light') return stored;
    } catch {
      // localStorage inaccessibile (private browsing, storage disabled).
      // Non è un bug bloccante — proseguiamo col fallback prefers-color-scheme.
    }
    return null;
  }

  /**
   * Salva la preferenza. Errori silenti (idempotente): se localStorage
   * non è disponibile, il tema resta valido per la sessione corrente
   * ma non persiste tra reload.
   */
  private persistTheme(theme: ThemeMode): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, theme);
    } catch {
      // ignore
    }
  }

  /**
   * Query al sistema operativo. Ritorna `true` se l'utente ha settato
   * "dark" nelle preferenze OS (macOS System Settings → Appearance,
   * Windows → Colors → Dark). Fallback safe se `matchMedia` non è
   * disponibile (test env / vecchi browser).
   */
  private prefersSystemDark(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
}
